<template>
    <Dialog :open="open" @update:open="$emit('close')">
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>
                    <span v-if="isResuming">Resume your subscription</span>
                    <span v-else-if="isCanceling">Cancel your subscription</span>
                    <span v-else>Change your subscription</span>
                </DialogTitle>
            </DialogHeader>

            <div class="space-y-4">
                <!-- Current subscription summary -->
                <div
                    class="flex flex-col rounded-md p-3 sm:flex-row sm:items-start sm:gap-3"
                    :class="isCanceling ? 'bg-new-red-100 text-new-red-700' : 'bg-new-blue-50 text-new-blue-700'"
                >
                    <Info class="size-5 shrink-0" />
                    <p class="mt-2 text-sm sm:mt-0">
                        You are currently using
                        <span class="font-medium">
                            {{ proSubscription.period === 1 ? 'Month-to-month' : 'Annual' }}
                            subscription plan for Topol {{ team.personal_team ? 'PRO' : 'Team' }}</span>
                        <span v-if="!team.personal_team">
                            , including {{ proSubscription.totalPaidSeats }} pre-paid member seats.
                        </span>
                        <span v-else>, including 1 member seat.</span>
                        <span v-if="!proSubscription.canceled_at && proSubscription.next_billing_date">
                            Your plan
                            <span v-if="new Date(proSubscription.next_billing_date) > new Date()">will renew on</span>
                            <span v-else>was about to renew on</span>
                            {{ format(new Date(proSubscription.next_billing_date), 'd MMMM, yyyy') }}.
                        </span>
                    </p>
                </div>

                <!-- Plan selector -->
                <div>
                    <Label class="text-sm font-medium text-foreground">Choose your subscription</Label>
                    <Select v-model="selectedPlanUuid" class="mt-2">
                        <SelectTrigger>
                            <SelectValue placeholder="Choose subscription" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem
                                v-for="option in proPlanOptions"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <!-- Pricing for switch -->
                <div v-if="preparedSub && !isCanceling && !isResuming" class="rounded-md bg-new-neutral-50 p-4 space-y-2 text-sm">
                    <div class="flex justify-between">
                        <span class="text-muted-foreground">New plan</span>
                        <span class="font-medium text-foreground">{{ preparedSub.subscription_plan?.name }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-2">
                        <span class="text-muted-foreground">Price</span>
                        <span class="flex items-baseline gap-2">
                            <span v-if="preparedSub.total_without_sale && preparedSub.total_without_sale !== preparedSub.total" class="text-xs text-muted-foreground line-through">
                                ${{ (preparedSub.total_without_sale / 100).toFixed(2) }}
                            </span>
                            <span class="font-medium text-foreground">
                                ${{ (preparedSub.total / 100).toFixed(2) }}/{{ preparedSub.subscription_plan?.period === 12 ? 'year' : 'month' }}
                            </span>
                        </span>
                    </div>
                    <div v-if="proSubscription.next_billing_date && new Date(proSubscription.next_billing_date) > new Date()" class="flex justify-between">
                        <span class="text-muted-foreground">{{ preparedSub.subscription_plan?.period === 1 ? 'Month-to-month' : 'Annual' }} billing starts on</span>
                        <span class="font-medium text-foreground">
                            {{ format(new Date(proSubscription.next_billing_date), 'd MMMM, yyyy') }}
                        </span>
                    </div>
                    <div v-else class="flex justify-between">
                        <span class="text-muted-foreground">Billing starts on</span>
                        <span class="font-medium text-foreground">{{ format(new Date(), 'd MMMM, yyyy') }}</span>
                    </div>
                </div>

                <!-- Resume context -->
                <div v-if="isResuming" class="flex items-start gap-3 rounded-md bg-new-blue-50 p-3">
                    <TriangleAlert class="mt-0.5 size-5 shrink-0 text-new-blue-600" />
                    <p class="text-sm text-new-blue-700">
                        Your subscription is currently scheduled to end. Resuming will keep it active.
                    </p>
                </div>

                <!-- Cancel context -->
                <div v-if="isCanceling" class="flex items-start gap-3 rounded-md bg-new-amber-100 p-3">
                    <TriangleAlert class="mt-0.5 size-5 shrink-0 text-new-amber-600" />
                    <div class="text-sm text-new-amber-800 space-y-2">
                        <p v-if="proSubscription.next_billing_date && new Date(proSubscription.next_billing_date) > new Date()">
                            When switching to free plan,
                            <span class="font-medium">you will be able to use Topol PRO features
                            <span v-if="!team.personal_team">for {{ proSubscription.totalPaidSeats }} member seats</span>
                            until {{ format(new Date(proSubscription.next_billing_date), 'd MMMM, yyyy') }}.</span>
                            After this date you will be switched to FREE plan without PRO and Team features.
                        </p>
                        <p>
                            Note that after your subscription is canceled, you will lose access to the images saved in our storage.
                        </p>
                    </div>
                </div>

                <!-- Confirmation -->
                <label class="flex items-start gap-3 cursor-pointer">
                    <input
                        v-model="confirmed"
                        type="checkbox"
                        class="mt-0.5 size-4 rounded border-new-neutral-300"
                    />
                    <span class="text-sm text-muted-foreground">
                        <span v-if="isResuming">I confirm that I want to resume my subscription</span>
                        <span v-else-if="isCanceling">I confirm that I want to CANCEL my subscription</span>
                        <span v-else>I confirm that I want to change my subscription</span>
                    </span>
                </label>
            </div>

            <DialogFooter class="mt-4">
                <Button variant="outline" @click="$emit('close')">Cancel</Button>
                <Button
                    :variant="isCanceling ? 'destructive' : 'default'"
                    :disabled="submitDisabled"
                    @click="submit"
                >
                    <span v-if="isResuming">Resume</span>
                    <span v-else-if="isCanceling">Cancel subscription and switch to FREE plan</span>
                    <span v-else>Change subscription</span>
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch, PropType } from 'vue';
import axios from 'axios';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { format } from 'date-fns';
import { Info, TriangleAlert } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import SubscriptionPlan from '@/types/Subscription/SubscriptionPlan';
import ISubscription from '@/types/Subscription/ISubscription';
import { ICreditCardPaymentMethod, IPaymentMethod } from '@/types/Subscription/IPaymentMethod';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import useFeatures from '@/composables/useFeatures';
import { useBraintreeClient } from '@/composables/useBraintree';
import { useGtm } from '@/composables/useGtm';

const props = defineProps({
    open: {
        type: Boolean,
        required: true,
    },
    proSubscription: {
        type: Object as PropType<ISubscription>,
        required: true,
    },
    selectedCard: {
        type: Object as PropType<IPaymentMethod | null>,
        default: null,
    },
    subscriptionPlans: {
        type: Array as PropType<SubscriptionPlan[]>,
        required: true,
    },
});

const emit = defineEmits<{
    close: [];
    canceled: [];
}>();

const team = computed(() => usePage<ITopolPageProps>().props.team);
const { teamFeatureDisabled } = useFeatures();
const { pushEvent } = useGtm();
const { createClient: createBraintreeClient, verifyCardByToken } = useBraintreeClient('pro');

const isStripeEnabled = !teamFeatureDisabled('stripe');

const CANCEL_VALUE = '__cancel__';
const currentPlanUuid = computed(() => props.proSubscription.subscription_plan_uuid);
const selectedPlanUuid = ref<string>(currentPlanUuid.value);
const confirmed = ref(false);
const loading = ref(false);
const preparedSub = ref<any>(null);

const isCanceling = computed(() => selectedPlanUuid.value === CANCEL_VALUE);
const isResuming = computed(
    () =>
        selectedPlanUuid.value === currentPlanUuid.value &&
        !!props.proSubscription.canceled_at
);

const submitDisabled = computed(() => {
    if (!confirmed.value || loading.value) return true;
    if (isResuming.value) return false;
    if (isCanceling.value) return !!props.proSubscription.canceled_at;
    if (selectedPlanUuid.value === currentPlanUuid.value) return true;
    return !preparedSub.value;
});

const proPlanOptions = computed(() => {
    const filtered = props.subscriptionPlans.filter((plan) =>
        team.value.personal_team ? !plan.uuid.includes('team') : plan.uuid.includes('team')
    );

    const options = filtered.map((plan) => ({
        value: plan.uuid,
        label: `Topol ${plan.name}${plan.uuid === currentPlanUuid.value ? ' (current)' : ''}`,
    }));

    options.push({
        value: CANCEL_VALUE,
        label: 'Topol FREE - Free plan',
    });

    return options;
});

watch(selectedPlanUuid, async (uuid) => {
    confirmed.value = false;
    preparedSub.value = null;

    if (uuid && uuid !== CANCEL_VALUE && uuid !== currentPlanUuid.value) {
        try {
            const res = await axios.get(
                route('subscriptions.prepare', { subscription_plan_uuid: uuid })
            );
            preparedSub.value = res.data.data;
        } catch {
            // swallow — UI will show no pricing block
        }
    }
});

watch(() => props.open, (val) => {
    if (val) {
        selectedPlanUuid.value = currentPlanUuid.value;
        confirmed.value = false;
        preparedSub.value = null;
    }
});

const submit = async () => {
    if (submitDisabled.value) return;
    loading.value = true;

    try {
        if (isResuming.value) {
            await resumeSubscription();
        } else if (isCanceling.value) {
            await cancelSubscription();
        } else {
            await changeSubscription();
        }
    } catch {
        // toast already shown in inner functions
    } finally {
        loading.value = false;
    }
};

const changeSubscription = async () => {
    if (!props.selectedCard?.token) {
        toast.error('Please select a payment method before changing your subscription.');
        throw new Error('Card not selected');
    }

    const changeRoute = isStripeEnabled
        ? route('stripe.subscriptions.update', { subscription: props.proSubscription.id })
        : route('subscriptions.switch', { subscription: props.proSubscription.id });

    const method = isStripeEnabled ? 'put' : 'post';

    let verifyCardResponse: { nonce: string } | undefined;
    if (!isStripeEnabled) {
        await createBraintreeClient({ enableHostedFields: false });

        if ((props.selectedCard as ICreditCardPaymentMethod).bin !== undefined) {
            verifyCardResponse = await verifyCardByToken({
                token: props.selectedCard.token,
                amount: preparedSub.value?.total ?? 0,
            });
        }
    }

    return new Promise((resolve, reject) => {
        router[method](
            changeRoute,
            {
                subscription_plan_uuid: selectedPlanUuid.value,
                nonce: verifyCardResponse ? verifyCardResponse.nonce : null,
            },
            {
                preserveState: false,
                preserveScroll: true,
                onSuccess: (response) => {
                    toast.success('Subscription switched');
                    emit('close');
                    resolve(response);
                },
                onError: (errors: Record<string, string>) => {
                    const message = Object.values(errors)[0] ?? 'Failed to switch subscription';
                    toast.error(message);
                    reject(errors);
                },
            }
        );
    });
};

const cancelSubscription = () => {
    const cancelRoute = isStripeEnabled
        ? route('stripe.subscriptions.cancel', { subscription: props.proSubscription.id })
        : route('subscriptions.cancel', { subscription: props.proSubscription.id });

    return new Promise((resolve, reject) => {
        router.post(cancelRoute, {}, {
            preserveScroll: true,
            onSuccess: (response) => {
                toast.success('Subscription canceled');
                emit('canceled');
                emit('close');
                resolve(response);
            },
            onError: (errors: Record<string, string>) => {
                const message = Object.values(errors)[0] ?? 'Failed to cancel subscription';
                toast.error(message);
                reject(errors);
            },
        });
    });
};

const resumeSubscription = () => {
    // Stripe's subscriptions.resume API only works for *paused* subscriptions,
    // not canceled ones. Once a subscription is canceled in Stripe, the only
    // way back is to start a new subscription via the order flow. Match v1.
    if (isStripeEnabled) {
        toast.error('Your subscription has been canceled. To restart, please subscribe to a plan again from the Subscription page.');
        throw new Error('Stripe resume not supported for canceled subscriptions');
    }

    return new Promise((resolve, reject) => {
        router.post(
            route('subscriptions.resume', { subscription: props.proSubscription.id }),
            {},
            {
                preserveScroll: true,
                onSuccess: (response) => {
                    toast.success('Subscription resumed');
                    pushEvent('topol_purchase_renewal', {
                        type: 'pro',
                        amountPaid: (preparedSub.value?.total ?? 0) / 100,
                        planType: preparedSub.value?.subscription_plan?.period === 1 ? 'monthly' : 'yearly',
                        currency: 'USD',
                    });
                    emit('close');
                    resolve(response);
                },
                onError: (errors: Record<string, string>) => {
                    const message = Object.values(errors)[0] ?? 'Failed to resume subscription';
                    toast.error(message);
                    reject(errors);
                },
            }
        );
    });
};
</script>
